/* eslint-disable camelcase */
import { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import { results, HorizontalTable } from '@mdc/ui';
import { useTranslation } from 'react-i18next';
import MITRE_DATA from './MitreData';
import ExpandableRow from '../../dynamic-analysis/ExpandableRow';
import OpswatMitre from './OpswatMitre.js';

import './Mitre.scss';

const EXISTING_MITRE_VALUES = (t) => [
    {
        key: 'mitre_ics',
        name: t('ICS Matrix')
    },
    {
        key: 'mitre_mobile',
        name: t('Mobile Matrix')
    },
    {
        key: 'mitre',
        name: t('Enterprise Matrix')
    }
];

const TACTICS = (t) => ({
    'reconnaissance': t('Reconnaissance'),
    'resource-development': t('Resource Development'),
    'initial-access': t('Initial Access'),
    'execution': t('Execution'),
    'persistence': t('Persistence'),
    'privilege-escalation': t('Privilege Escalation'),
    'defense-evasion': t('Defense Evasion'),
    'credential-access': t('Credential Access'),
    'discovery': t('Discovery'),
    'lateral-movement': t('Lateral Movement'),
    'collection': t('Collection'),
    'command-and-control': t('Command and Control'),
    'exfiltration': t('Exfiltration'),
    'impact': t('Impact')
});

const COLORS_BY_NUMBER = {
    'turmericRoot': { min: 1, max: 1 },
    'redStop': { min: 2, max: 2 },
    'rebellionRed': { min: 3, max: undefined },
};

const { PlainTablePlaceholder, SandboxTabs, MitreTabs, MitreContext } = results;

const Mitre = ({ sandboxResults }) => {
    const mitreData = useMemo(() => sandboxResults?.data?.mitreData || sandboxResults?.data?.details?.SignaturesMeta, [sandboxResults]);
    const [activeMitreKey, setActiveMitreKey] = useState('mitre');
    const signaturesMetaData = useMemo(() => sandboxResults?.data?.details?.SignaturesMeta ? sandboxResults?.data?.details?.signatures?.metadata : {}, [sandboxResults]);
    const { t, ready } = useTranslation();

    const opswatMitreData = useMemo(() => sandboxResults?.data?.details?.SignaturesMeta && mitreData?.reduce((acc, item) => {
        EXISTING_MITRE_VALUES(t).forEach((mitreValue) => {
            if (item?.ttp?.hasOwnProperty(mitreValue.key)) {
                const metaDataTactics = signaturesMetaData?.hasOwnProperty(mitreValue.key) ? signaturesMetaData[mitreValue.key]?.tactics : {};
                acc[mitreValue.key].push(item?.ttp[mitreValue.key]);

                if (!acc[mitreValue.key]?.tactics && metaDataTactics) {
                    acc[mitreValue.key]['tactics'] = metaDataTactics;
                }
            }
        });

        return acc;
    }, { mitre: [], mitre_ics: [], mitre_mobile: [] }), [mitreData, sandboxResults, signaturesMetaData]);

    const getProcessedRawDataValue = (value, row, data) => {
        if (Array.isArray(value)) {
            return value.join(', ');
        }

        value += row.accessor === 'id' ? `&nbsp; <a href='${data?.url}' rel='noopener noreferrer' target='_blank'>${data?.url}</a>` : null;
        return value;
    };

    const processRawData = (data, meta) => meta.filter((row) => Object.hasOwnProperty.call(data, row.accessor) && (data[row.accessor]))
        .map((row) => {
            let value = row.parser ? row.parser(data[row.accessor]) : data[row.accessor];
            value = getProcessedRawDataValue(value, row, data);

            return { field: row.header, data: value, shouldSanitizeHTML: true };
        });

    const expandableRowDom = (mitre) => {
        return (
            <Fragment>
                <HorizontalTable data={processRawData(mitre, MITRE_DATA(t))} />
            </Fragment>
        );
    };

    const generateDomData = (mitreArray) => {
        const color = Object.keys(COLORS_BY_NUMBER).filter((color) => COLORS_BY_NUMBER[color].min <= mitreArray?.length && (COLORS_BY_NUMBER[color].max >= mitreArray?.length || COLORS_BY_NUMBER[color].max === undefined));
        return mitreArray?.map((mitre, key) => {
            return <ExpandableRow
                title={mitre?.name}
                tableDom={expandableRowDom(mitre)}
                titleSize='sm'
                extraHeaderFields={[mitreArray.length]}
                extraHeaderPosition={'start'}
                extraHeaderColor={color}
                key={key}
            />;
        });
    };

    const groupBy = (info, key) => {
        return info.reduce((data, value) => {
            (data[value[key]] || []).push(value);
            return data;
        }, {});
    };

    const mitreDataDom = useMemo(() => {
        if (!mitreData || (Array.isArray(mitreData) && mitreData?.length === 0)) {
            return <PlainTablePlaceholder numberOfColumns={6} />;
        }

        const mitreDataGrouppedByTactic = groupBy(mitreData, 'tactics');

        return Object.keys(mitreDataGrouppedByTactic).map((key) => {
            if (Array.isArray(mitreDataGrouppedByTactic[key])) {
                const tacticsNames = key.split(',');
                const tactics = tacticsNames.map((tactic, key) => {
                    return <span key={key}>
                        {TACTICS(t)[tactic] || t('Mitigation')}
                        {tacticsNames.length > 1 && key < tacticsNames.length - 1 ? ', ' : ''}
                    </span>;
                });

                return (
                    <Row className='mt-4' key={key}>
                        <Col lg={3} md={3} xs={12}>
                            <p className='leftHeadline'>
                                {tactics}
                                <span className='d-md-none ml-1'>({mitreDataGrouppedByTactic[key].length || 0}/10)</span>
                            </p>
                        </Col>
                        <Col lg={9} md={9} xs={12} className='mitreDataWrapper'>
                            <p className='leftHeadline d-none d-md-block'>
                                {t('Techniques')}
                                <span className='ml-1'>({mitreDataGrouppedByTactic[key].length || 0}/10)</span>
                            </p>
                            {generateDomData(mitreDataGrouppedByTactic[key])}
                        </Col>
                    </Row>
                );
            }

            return;
        });
    }, [mitreData]);

    const onSelectMitreChange = useMemo(() => (key) => {
        if (!opswatMitreData) {
            return;
        }

        setActiveMitreKey(key);
    }, [opswatMitreData]);

    const mitreContextValue = useMemo(() => {
        if (!opswatMitreData) {
            return;
        }

        return ({
            mitreData: opswatMitreData[activeMitreKey],
            activeMitreKey: activeMitreKey,
            onSelectMitreChange
        });
    }, [opswatMitreData]);

    const mitreDom = useMemo(() => {
        if (!sandboxResults?.data || !sandboxResults) {
            return <PlainTablePlaceholder numberOfColumns={6}/>;
        }

        if (sandboxResults?.data?.summary) {
            return <SandboxTabs className='sandboxMitreSection dynamicAnalysis' isNoData={sandboxResults?.data?.mitreData?.length === 0}>
                <div className='d-md-none mitreTitle'>{t('MITRE ATT&CK Matrix')}</div>
                {mitreDataDom}
            </SandboxTabs>;
        }

        // remove the condition below
        // after the Mitre implementation is done on Platform's side
        if (sandboxResults?.data?.details?.Dynamic) {
            return;
        }

        return <MitreContext.Provider value={mitreContextValue}>
            <MitreTabs>
                <OpswatMitre
                    mitreData={opswatMitreData[activeMitreKey]}
                    activeMitreKey={activeMitreKey}
                    sandboxResults={sandboxResults}
                    mitreValues={EXISTING_MITRE_VALUES}
                />
            </MitreTabs>
        </MitreContext.Provider>;
    }, [sandboxResults, activeMitreKey, mitreDataDom, mitreData, t]);

    if (!ready) {
        return null;
    }

    return <>{mitreDom}</>;
};

Mitre.propTypes = {
    sandboxResults: PropTypes.object
};

export default Mitre;
